import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../../api';

/* ── tiny helpers ─────────────────────────────────────────────── */
const fmt  = n => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';
const ago  = d => {
  if (!d) return '';
  const diff = Math.floor((Date.now() - new Date(d)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff <  7 ) return `${diff}d ago`;
  return new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short' });
};

const STATUS_STYLE = {
  operational: { bg:'#dcfce7', color:'#15803d', dot:'#16a34a', label:'Operational' },
  maintenance:  { bg:'#fef3c7', color:'#b45309', dot:'#d97706', label:'Maintenance'  },
  breakdown:    { bg:'#fee2e2', color:'#dc2626', dot:'#dc2626', label:'Breakdown'    },
  retired:      { bg:'#f1f5f9', color:'#64748b', dot:'#94a3b8', label:'Retired'      },
  open:         { bg:'#fef3c7', color:'#b45309', dot:'#d97706', label:'Open'         },
  assigned:     { bg:'#dbeafe', color:'#1a56db', dot:'#1a56db', label:'Assigned'     },
  in_progress:  { bg:'#dbeafe', color:'#1a56db', dot:'#1a56db', label:'In Progress'  },
  closed:       { bg:'#dcfce7', color:'#15803d', dot:'#16a34a', label:'Closed'       },
  cancelled:    { bg:'#f1f5f9', color:'#64748b', dot:'#94a3b8', label:'Cancelled'    },
  pm:           { bg:'#dbeafe', color:'#1a56db', dot:'#1a56db', label:'PM'           },
  breakdown_wo: { bg:'#fee2e2', color:'#dc2626', dot:'#dc2626', label:'Breakdown'    },
  inspection:   { bg:'#f0fdf4', color:'#15803d', dot:'#16a34a', label:'Inspection'   },
  repair:       { bg:'#fef3c7', color:'#b45309', dot:'#d97706', label:'Repair'       },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.retired;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5,
      padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:600,
      background:s.bg, color:s.color }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:s.dot, flexShrink:0 }}/>
      {s.label || status}
    </span>
  );
}

function WOTypeBadge({ type }) {
  const MAP = {
    pm:          { bg:'#dbeafe', color:'#1a56db', label:'PM'          },
    breakdown:   { bg:'#fee2e2', color:'#dc2626', label:'Breakdown'   },
    inspection:  { bg:'#f0fdf4', color:'#15803d', label:'Inspection'  },
    repair:      { bg:'#fef3c7', color:'#b45309', label:'Repair'      },
    calibration: { bg:'#ede9fe', color:'#7c3aed', label:'Calibration' },
  };
  const s = MAP[type] || { bg:'#f1f5f9', color:'#64748b', label: type || '—' };
  return (
    <span style={{ padding:'2px 8px', borderRadius:5, fontSize:11,
      fontWeight:600, background:s.bg, color:s.color }}>{s.label}</span>
  );
}

/* ── KPI Card ──────────────────────────────────────────────────── */
function KpiCard({ icon, label, value, sub, subColor, accent, trend }) {
  return (
    <div style={{
      background:'#fff', borderRadius:12, padding:'18px 20px',
      border:'1px solid #e2e8f0', position:'relative', overflow:'hidden',
      transition:'box-shadow .2s', cursor:'default',
    }}
    onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 20px rgba(0,0,0,0.08)'}
    onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
      {/* top accent bar */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
        borderRadius:'12px 12px 0 0', background: accent || '#1a56db' }} />
      <div style={{ alignItems:'flex-start', justifyContent:'space-between' }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#94a3b8',
            textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>
            {label}
          </div>
          <div style={{ fontSize:30, fontWeight:800, color:'#1a1f2e', lineHeight:1, marginBottom:6 }}>
            {value ?? <span style={{ color:'#cbd5e1' }}>—</span>}
          </div>
          {sub && (
            <div style={{ fontSize:12, color: subColor || '#64748b', display:'flex', alignItems:'center', gap:4 }}>
              {trend && <span style={{ fontSize:11, fontWeight:700 }}>{trend}</span>}
              {sub}
            </div>
          )}
        </div>
        <div style={{ fontSize:26, opacity:.12, marginLeft:8, marginTop:2 }}>{icon}</div>
      </div>
    </div>
  );
}

/* ── Mini bar chart ────────────────────────────────────────────── */
function MiniBarChart({ data }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:4, height:60 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
          <div style={{
            width:'100%', borderRadius:'4px 4px 0 0',
            height: `${Math.max((d.value / max) * 52, 2)}px`,
            background: d.color || '#1a56db',
            opacity: d.dim ? .4 : .85,
            transition:'height .4s ease',
          }} title={`${d.label}: ${d.value}`} />
          <span style={{ fontSize:9, color:'#94a3b8', whiteSpace:'nowrap' }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Donut chart ───────────────────────────────────────────────── */
function DonutChart({ segments, total }) {
  const size = 88, stroke = 14, r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const arcs = segments.map(s => {
    const dash = (s.value / Math.max(total, 1)) * circ;
    const arc = { ...s, dash, offset };
    offset += dash;
    return arc;
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform:'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke="#f1f5f9" strokeWidth={stroke} />
      {arcs.map((a, i) => (
        <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
          stroke={a.color} strokeWidth={stroke}
          strokeDasharray={`${a.dash} ${circ - a.dash}`}
          strokeDashoffset={-a.offset}
          strokeLinecap="butt" />
      ))}
      <text x={size/2} y={size/2} textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize:14, fontWeight:800, fill:'#1a1f2e', transform:'rotate(90deg)',
          transformOrigin:'center' }}>
        {total}
      </text>
    </svg>
  );
}

/* ── Skeleton loader ───────────────────────────────────────────── */
function Skeleton({ w='100%', h=16, r=6 }) {
  return (
    <div style={{
      width:w, height:h, borderRadius:r,
      background:'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)',
      backgroundSize:'200% 100%',
      animation:'shimmer 1.4s infinite',
    }} />
  );
}

/* ═══════════════════════════════════════════════════════════════ */
export default function Dashboard() {
  const navigate  = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    dashboardAPI.stats()
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => {
        console.error('[Dashboard] stats failed:', e.response?.data || e.message);
        setError(e.response?.data?.detail || 'Failed to load dashboard');
        setLoading(false);
      });
  }, []);

  /* ── derived values ── */
  const assets    = data?.assets       || {};
  const wo        = data?.work_orders  || {};
  const pm        = data?.pm_schedules || {};
  const spares    = data?.spares       || {};
  const comp      = data?.compliance   || {};
  const recentWOs = data?.recent_work_orders || [];
  const recentAssets = data?.recent_assets   || [];

  const totalAssets  = assets.total       ?? 0;
  const operational  = assets.operational ?? 0;
  const maint        = assets.maintenance ?? 0;
  const broken       = assets.breakdown   ?? 0;

  const uptimePct = totalAssets > 0
    ? Math.round((operational / totalAssets) * 100) : 0;

  const woTotal = wo.total    ?? 0;
  const woOpen  = wo.open     ?? 0;

  /* ── health donut segments ── */
  const donutSegs = [
    { color:'#16a34a', value: operational, label:'Operational' },
    { color:'#d97706', value: maint,       label:'Maintenance'  },
    { color:'#dc2626', value: broken,      label:'Breakdown'    },
    { color:'#94a3b8', value: (assets.retired ?? 0), label:'Retired' },
  ].filter(s => s.value > 0);

  /* ── shimmer CSS ── */
  const shimmerCSS = `
    @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
    @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
    .dash-card { animation: fadeUp .3s ease both; }
  `;

  if (error) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', height:'60vh', gap:16, color:'#94a3b8' }}>
      <div style={{ fontSize:40 }}>⚠️</div>
      <div style={{ fontSize:16, fontWeight:700, color:'#dc2626' }}>Dashboard failed to load</div>
      <div style={{ fontSize:13, color:'#64748b', maxWidth:400, textAlign:'center' }}>{error}</div>
      <button onClick={() => window.location.reload()}
        style={{ padding:'8px 20px', background:'#1a56db', color:'#fff', border:'none',
          borderRadius:8, fontSize:13, cursor:'pointer', fontWeight:600 }}>
        Retry
      </button>
    </div>
  );

  return (
    <div style={{ padding:24, background:'#f8fafc', minHeight:'100%' }}>
      <style>{shimmerCSS}</style>

      {/* ── Page header ──────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between',
        marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:'#1a1f2e', margin:0, lineHeight:1.2 }}>
            Dashboard
          </h1>
          <p style={{ fontSize:13, color:'#64748b', margin:'4px 0 0' }}>
            {new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
          </p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
        {/*   <button onClick={() => navigate('/assets/new')}
            style={{ padding:'8px 16px', background:'#1a56db', color:'#fff', border:'none',
              borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
              display:'flex', alignItems:'center', gap:6 }}>
            + New Asset
          </button>
          <button onClick={() => navigate('/work-orders')}
            style={{ padding:'8px 16px', background:'#fff', color:'#374151',
              border:'1px solid #e2e8f0', borderRadius:8, fontSize:13, fontWeight:500,
              cursor:'pointer' }}>
            Work Orders
          </button> */}
        </div>
      </div>

      {/* ── KPI strip ────────────────────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',
        gap:14, marginBottom:20 }}>
        {loading ? Array(5).fill(0).map((_, i) => (
          <div key={i} style={{ background:'#fff', borderRadius:12, padding:20,
            border:'1px solid #e2e8f0' }}>
            <Skeleton h={10} w="60%" r={4} />
            <div style={{ marginTop:12 }}><Skeleton h={28} w="40%" r={4} /></div>
            <div style={{ marginTop:8 }}><Skeleton h={10} w="80%" r={4} /></div>
          </div>
        )) : (<>
          <KpiCard icon="◼" label="Total Assets"    value={totalAssets}
            sub={`${uptimePct}% uptime`} subColor="#16a34a"
            accent="#1a56db" trend={uptimePct >= 80 ? '↑' : '↓'} />
          <KpiCard icon="✓"  label="Operational"   value={operational}
            sub={`${totalAssets > 0 ? Math.round(operational/totalAssets*100) : 0}% of fleet`}
            accent="#16a34a" />
          <KpiCard icon="🔧" label="Under Maintenance" value={maint}
            sub={`${pm.overdue ?? 0} PM overdue`} subColor={pm.overdue > 0 ? '#dc2626' : '#64748b'}
            accent="#d97706" />
          <KpiCard icon="⚡" label="Breakdowns"    value={broken}
            sub={`${wo.breakdown ?? 0} open WOs`} subColor={broken > 0 ? '#dc2626' : '#64748b'}
            accent="#dc2626" />
          <KpiCard icon="📋" label="Open WOs"      value={woOpen}
            sub={`${woTotal} total WOs`}
            accent="#7c3aed" />
        </>)}
      </div>

      {/* ── Row 2: Health donut + WO breakdown + Alerts ──────── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:20 }}>

        {/* Asset Health */}
        <div className="dash-card" style={{ animationDelay:'.05s',
          background:'#fff', borderRadius:12, padding:20, border:'1px solid #e2e8f0' }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#1a1f2e', marginBottom:16 }}>
            Asset Health Status
          </div>
          {loading ? (
            <div style={{ display:'flex', gap:16, alignItems:'center' }}>
              <Skeleton w={88} h={88} r={44} />
              <div style={{ flex:1, display:'flex', flexDirection:'column', gap:8 }}>
                {[1,2,3].map(i => <Skeleton key={i} h={12} r={4} />)}
              </div>
            </div>
          ) : (
            <div style={{ display:'flex', gap:16, alignItems:'center' }}>
              <DonutChart segments={donutSegs} total={totalAssets} />
              <div style={{ flex:1, display:'flex', flexDirection:'column', gap:8 }}>
                {[
                  { color:'#16a34a', label:'Operational', value:operational },
                  { color:'#d97706', label:'Maintenance',  value:maint },
                  { color:'#dc2626', label:'Breakdown',    value:broken },
                  { color:'#94a3b8', label:'Retired',      value:assets.retired ?? 0 },
                ].map(s => (
                  <div key={s.label} style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:8, height:8, borderRadius:2, background:s.color, flexShrink:0 }}/>
                    <span style={{ flex:1, fontSize:12, color:'#64748b' }}>{s.label}</span>
                    <span style={{ fontSize:12, fontWeight:700, color:'#1a1f2e' }}>{s.value}</span>
                    <span style={{ fontSize:11, color:'#94a3b8', width:32, textAlign:'right' }}>
                      {totalAssets > 0 ? Math.round(s.value/totalAssets*100) : 0}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* WO Summary */}
        <div className="dash-card" style={{ animationDelay:'.1s',
          background:'#fff', borderRadius:12, padding:20, border:'1px solid #e2e8f0' }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#1a1f2e', marginBottom:16 }}>
            Work Order Summary
          </div>
          {loading ? (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {[1,2,3,4].map(i => <Skeleton key={i} h={36} r={8} />)}
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {[
                { label:'Total WOs',    value:woTotal,           color:'#1a56db', bg:'#dbeafe' },
                { label:'Open / Assigned', value:woOpen,         color:'#d97706', bg:'#fef3c7' },
                { label:'In Progress',  value:wo.in_progress??0, color:'#1a56db', bg:'#dbeafe' },
                { label:'Breakdown WOs',value:wo.breakdown??0,   color:'#dc2626', bg:'#fee2e2' },
                { label:'PM WOs',       value:wo.pm??0,          color:'#15803d', bg:'#dcfce7' },
              ].map(r => (
                <div key={r.label} style={{ display:'flex', alignItems:'center',
                  justifyContent:'space-between', padding:'8px 12px',
                  background:r.bg, borderRadius:8 }}>
                  <span style={{ fontSize:12, color: r.color, fontWeight:500 }}>{r.label}</span>
                  <span style={{ fontSize:16, fontWeight:800, color: r.color }}>{r.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Alerts */}
        <div className="dash-card" style={{ animationDelay:'.15s',
          background:'#fff', borderRadius:12, padding:20, border:'1px solid #e2e8f0' }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#1a1f2e', marginBottom:16 }}>
            Alerts & Attention
          </div>
          {loading ? (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {[1,2,3].map(i => <Skeleton key={i} h={52} r={8} />)}
            </div>
          ) : (() => {
            const alerts = [
              broken > 0 && {
                icon:'⚡', color:'#dc2626', bg:'#fee2e2',
                title: `${broken} asset${broken>1?'s':''} in breakdown`,
                sub: 'Requires immediate attention',
              },
              (pm.overdue ?? 0) > 0 && {
                icon:'🔧', color:'#d97706', bg:'#fef3c7',
                title: `${pm.overdue} PM task${pm.overdue>1?'s':''} overdue`,
                sub: 'Schedule maintenance now',
              },
              (comp.expiring_soon ?? 0) > 0 && {
                icon:'📋', color:'#7c3aed', bg:'#ede9fe',
                title: `${comp.expiring_soon} compliance cert${comp.expiring_soon>1?'s':''} expiring`,
                sub: 'Renew within 30 days',
              },
              (spares.low_stock ?? 0) > 0 && {
                icon:'📦', color:'#d97706', bg:'#fef3c7',
                title: `${spares.low_stock} spare part${spares.low_stock>1?'s':''} low stock`,
                sub: 'Below reorder level',
              },
            ].filter(Boolean);

            return alerts.length === 0 ? (
              <div style={{ textAlign:'center', padding:'20px 0', color:'#16a34a' }}>
                <div style={{ fontSize:32, marginBottom:8 }}>✅</div>
                <div style={{ fontSize:13, fontWeight:600 }}>All systems normal</div>
                <div style={{ fontSize:12, color:'#64748b', marginTop:4 }}>No alerts at this time</div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {alerts.slice(0,4).map((a, i) => (
                  <div key={i} style={{ display:'flex', gap:10, padding:'10px 12px',
                    background:a.bg, borderRadius:8, alignItems:'flex-start' }}>
                    <span style={{ fontSize:16, flexShrink:0, marginTop:1 }}>{a.icon}</span>
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:a.color }}>{a.title}</div>
                      <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>{a.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── Row 3: Recent WOs + Recent Assets ─────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'4fr 2fr', gap:14 }}>

        {/* Recent Work Orders */}
        <div className="dash-card" style={{ animationDelay:'.2s',
          background:'#fff', borderRadius:12, border:'1px solid #e2e8f0', overflow:'hidden' }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9',
            display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#1a1f2e' }}>Recent Work Orders</div>
            <button onClick={() => navigate('/work-orders')}
              style={{ fontSize:12, color:'#1a56db', background:'none', border:'none',
                cursor:'pointer', fontWeight:600 }}>
              View all →
            </button>
          </div>

          {loading ? (
            <div style={{ padding:20, display:'flex', flexDirection:'column', gap:12 }}>
              {[1,2,3,4,5].map(i => <Skeleton key={i} h={44} r={8} />)}
            </div>
          ) : recentWOs.length === 0 ? (
            <div style={{ padding:'40px 20px', textAlign:'center', color:'#94a3b8' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>🔧</div>
              <div style={{ fontSize:13 }}>No work orders yet</div>
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  {['WO#', 'Asset', 'Type', 'Priority', 'Status', 'Created'].map(h => (
                    <th key={h} style={{ padding:'10px 14px', fontSize:11, fontWeight:700,
                      textTransform:'uppercase', letterSpacing:'.05em', color:'#94a3b8',
                      background:'#f8fafc', borderBottom:'1px solid #f1f5f9',
                      textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentWOs.map((wo, i) => (
                  <tr key={wo.id || i}
                    onClick={() => navigate(`/work-orders/${wo.id}`)}
                    style={{ borderBottom:'1px solid #f8fafc', cursor:'pointer',
                      transition:'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background=''}>
                    <td style={{ padding:'11px 14px', fontFamily:'monospace',
                      fontSize:12, color:'#1a56db', fontWeight:600 }}>
                      {wo.wo_number}
                    </td>
                    <td style={{ padding:'11px 14px', fontSize:13, color:'#1a1f2e',
                      fontWeight:500, maxWidth:160, overflow:'hidden',
                      textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {wo.asset_name || wo.asset_code || '—'}
                    </td>
                    <td style={{ padding:'11px 14px' }}>
                      <WOTypeBadge type={wo.type} />
                    </td>
                    <td style={{ padding:'11px 14px' }}>
                      <span style={{
                        padding:'2px 8px', borderRadius:5, fontSize:11, fontWeight:600,
                        background: wo.priority === 'critical' ? '#fee2e2'
                          : wo.priority === 'high' ? '#fef3c7'
                          : wo.priority === 'medium' ? '#dbeafe' : '#f1f5f9',
                        color: wo.priority === 'critical' ? '#dc2626'
                          : wo.priority === 'high' ? '#d97706'
                          : wo.priority === 'medium' ? '#1a56db' : '#64748b',
                      }}>
                        {wo.priority ? wo.priority.charAt(0).toUpperCase() + wo.priority.slice(1) : '—'}
                      </span>
                    </td>
                    <td style={{ padding:'11px 14px' }}>
                      <StatusBadge status={wo.status} />
                    </td>
                    <td style={{ padding:'11px 14px', fontSize:12, color:'#94a3b8',
                      whiteSpace:'nowrap' }}>
                      {ago(wo.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Right column: PM + Quick actions */}
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

          {/* PM Status */}
          <div className="dash-card" style={{ animationDelay:'.25s',
            background:'#fff', borderRadius:12, padding:20, border:'1px solid #e2e8f0' }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#1a1f2e', marginBottom:14 }}>
              PM Schedules
            </div>
            {loading ? (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {[1,2].map(i => <Skeleton key={i} h={48} r={8} />)}
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'12px 14px', background:'#f0fdf4', borderRadius:8 }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:'#15803d' }}>Scheduled</div>
                    <div style={{ fontSize:10, color:'#64748b', marginTop:2 }}>Total active PMs</div>
                  </div>
                  <div style={{ fontSize:22, fontWeight:800, color:'#15803d' }}>
                    {pm.total ?? 0}
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'12px 14px',
                  background:(pm.overdue ?? 0) > 0 ? '#fee2e2' : '#f8fafc',
                  borderRadius:8 }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700,
                      color:(pm.overdue ?? 0) > 0 ? '#dc2626' : '#64748b' }}>
                      Overdue
                    </div>
                    <div style={{ fontSize:10, color:'#64748b', marginTop:2 }}>Needs attention</div>
                  </div>
                  <div style={{ fontSize:22, fontWeight:800,
                    color:(pm.overdue ?? 0) > 0 ? '#dc2626' : '#94a3b8' }}>
                    {pm.overdue ?? 0}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="dash-card" style={{ animationDelay:'.3s',
            background:'#fff', borderRadius:12, padding:20, border:'1px solid #e2e8f0' }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#1a1f2e', marginBottom:14 }}>
              Quick Actions
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {[
                { icon:'⚡', label:'Report Breakdown',      color:'#dc2626', bg:'#fee2e2', path:'/work-orders' },
                { icon:'🔧', label:'Schedule PM',           color:'#d97706', bg:'#fef3c7', path:'/maintenance' },
                { icon:'◼',  label:'Register Asset',        color:'#1a56db', bg:'#dbeafe', path:'/assets/new'  },
                { icon:'📦', label:'Check Spare Parts',     color:'#7c3aed', bg:'#ede9fe', path:'/spares'      },
                { icon:'📋', label:'View Compliance',       color:'#15803d', bg:'#dcfce7', path:'/compliance'  },
              ].map(a => (
                <button key={a.label} onClick={() => navigate(a.path)}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
                    background:a.bg, border:'none', borderRadius:8, cursor:'pointer',
                    textAlign:'left', transition:'filter .15s' }}
                  onMouseEnter={e => e.currentTarget.style.filter='brightness(.95)'}
                  onMouseLeave={e => e.currentTarget.style.filter=''}>
                  <span style={{ fontSize:15 }}>{a.icon}</span>
                  <span style={{ fontSize:12, fontWeight:600, color:a.color }}>{a.label}</span>
                  <span style={{ marginLeft:'auto', fontSize:12, color:a.color, opacity:.6 }}>→</span>
                </button>
              ))}
            </div>
          </div>

          {/* Recently Added Assets */}
          {!loading && recentAssets.length > 0 && (
            <div className="dash-card" style={{ animationDelay:'.35s',
              background:'#fff', borderRadius:12, padding:20, border:'1px solid #e2e8f0' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                marginBottom:12 }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#1a1f2e' }}>
                  Recently Added
                </div>
                <button onClick={() => navigate('/assets')}
                  style={{ fontSize:11, color:'#1a56db', background:'none',
                    border:'none', cursor:'pointer', fontWeight:600 }}>
                  All assets →
                </button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {recentAssets.map((a, i) => (
                  <div key={a.id || i}
                    onClick={() => navigate(`/assets/${a.id}`)}
                    style={{ display:'flex', alignItems:'center', gap:10,
                      padding:'8px 0', borderBottom: i < recentAssets.length-1 ? '1px solid #f1f5f9':'none',
                      cursor:'pointer' }}>
                    <div style={{ width:32, height:32, background:'#dbeafe', borderRadius:8,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:14, flexShrink:0 }}>⚙</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'#1a1f2e',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {a.name}
                      </div>
                      <div style={{ fontSize:10, color:'#94a3b8', fontFamily:'monospace' }}>
                        {a.asset_code}
                      </div>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>{/* end row 3 */}
    </div>
  );
}
